import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { currentPublicBrand } from '../../lib/publicBrand';

const footerColumns = [
  {
    title: 'Product',
    links: [
      { href: '/', label: 'Overview' },
      { href: '/contact?type=platform_demo', label: 'Request invite' },
      { href: '/access', label: 'Sign in' },
    ],
  },
  {
    title: 'Access',
    links: [
      { href: '/access', label: 'Sign in' },
      { href: '/contact', label: 'Contact support' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { href: '/privacy', label: 'Privacy' },
      { href: '/terms', label: 'Terms' },
      { href: '/safeguarding', label: 'Safeguarding' },
      { href: '/whistleblowing', label: 'Whistleblowing' },
    ],
  },
];

export default function Footer({ minimal = false }) {
  const brand = currentPublicBrand();
  if (minimal) {
    return (
      <footer className={brand.isCareDock
        ? "caredock-platform-theme border-t border-[#CBE7F6] bg-[#F3FAFF] text-[#0D274E]"
        : "border-t border-slate-200 bg-white text-slate-600"}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm sm:flex-row sm:px-6 lg:px-8">
          {brand.isCareDock ? (
            <div className="flex flex-wrap items-center gap-4">
              <img src={brand.wordmark} alt="CareDock" className="h-6 w-auto max-w-[130px]" />
              <p>© {new Date().getFullYear()} {brand.name}</p>
            </div>
          ) : <p>© {new Date().getFullYear()} {brand.name}</p>}
          <div className="flex items-center gap-5">
            {brand.isCareDock ? (
              <>
                <Link to="/access" className="hover:text-primary">Access portal</Link>
                <Link to="/contact?type=platform_demo" className="hover:text-primary">Provider enquiries</Link>
                <Link to="/privacy" className="hover:text-primary">Privacy</Link>
                <Link to="/terms" className="hover:text-primary">Terms</Link>
              </>
            ) : (
              <>
                <Link to="/privacy" className="hover:text-primary">Privacy</Link>
                <Link to="/terms" className="hover:text-primary">Terms</Link>
                <Link to="/contact" className="hover:text-primary">Support</Link>
              </>
            )}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={brand.isCareDock
      ? "caredock-platform-theme bg-[#F3FAFF] text-[#0D274E]"
      : "bg-[#17324D] text-white"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-[1.2fr_2fr] gap-10">
          <div>
            <div className="flex items-center gap-3 mb-5">
              {brand.isCareDock ? (
                <img src={brand.wordmark} alt="CareDock" className="h-9 w-auto max-w-[190px]" />
              ) : (
                <>
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                    <span className="text-primary font-heading font-bold text-xl">O</span>
                  </div>
                  <span className="font-heading font-semibold text-xl">Osabea CareOps</span>
                </>
              )}
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-5">
              Invite-only workplace software for care-provider onboarding, checks, evidence and readiness decisions.
            </p>
            {brand.isCareDock ? (
              <p className="mb-2 text-sm leading-6 text-white/70">CareDock platform enquiries are not yet open. Provider contact details will be added once verified.</p>
            ) : (
            <div className="flex flex-col gap-3">
              <a href="tel:01622809469" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors text-sm">
                <Phone className="h-4 w-4" />
                <span>01622 809469</span>
              </a>
              <a href="mailto:info@osabeacares.co.uk" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors text-sm">
                <Mail className="h-4 w-4" />
                <span>info@osabeacares.co.uk</span>
              </a>
            </div>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {(brand.isCareDock
              ? [
                  { title: 'Platform', links: [{ href: '/', label: 'Overview' }, { href: '/contact', label: 'Provider enquiries' }, { href: '/access', label: 'Workspace access' }] },
                ]
              : footerColumns).map((column) => (
              <div key={column.title}>
                <h4 className="font-heading font-semibold text-lg mb-4">{column.title}</h4>
                <ul className="space-y-3">
                  {column.links.map((link) => (
                    <li key={`${column.title}-${link.href}-${link.label}`}>
                      <Link to={link.href} className="text-white/70 hover:text-white transition-colors text-sm">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <hr className="border-white/10 my-10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 flex-wrap justify-center md:justify-end">
            {!brand.isCareDock && (
              <>
                <Link to="/privacy" className="text-white/50 hover:text-white/70 text-sm transition-colors">Privacy</Link>
                <Link to="/terms" className="text-white/50 hover:text-white/70 text-sm transition-colors">Terms</Link>
              </>
            )}
            <Link to="/contact" className="text-white/50 hover:text-white/70 text-sm transition-colors">{brand.isCareDock ? 'Provider enquiries' : 'Contact'}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
